import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { SHOWS_LABEL, SHOWS_PLAYED, type SearchCriteria, type ShowsPlayed } from '../types';
import { BandPicker, type PickedBand } from '../components/BandPicker';
import { TagPicker } from '../components/TagPicker';
import { LocationPicker } from '../components/LocationPicker';
import { DEFAULT_RADIUS_MI, kmToMi, miToKm } from '../units';

/** Create or edit a search. "Run" executes without saving; "Save" persists it for later. */
export function SearchFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [minAge, setMinAge] = useState<string>('');
  const [maxAge, setMaxAge] = useState<string>('');
  const [loc, setLoc] = useState<{ city: string | null; lat: number | null; lng: number | null }>({ city: null, lat: null, lng: null });
  const [radiusMi, setRadiusMi] = useState(DEFAULT_RADIUS_MI);
  const [bandSource, setBandSource] = useState<'profile' | 'custom'>('profile');
  const [custom, setCustom] = useState<PickedBand[]>([]);
  const [instruments, setInstruments] = useState<string[]>([]);
  const [shows, setShows] = useState<ShowsPlayed[]>([]);
  const [presets, setPresets] = useState<string[]>([]);
  const [profileBands, setProfileBands] = useState<number | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api.instruments().then((r) => setPresets(r.instruments)).catch(() => {}); }, []);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const s = await api.getSearch(id);
          setName(s.name);
          setMinAge(s.minAge?.toString() ?? '');
          setMaxAge(s.maxAge?.toString() ?? '');
          setLoc({ city: s.city, lat: s.lat, lng: s.lng });
          setRadiusMi(Math.round(kmToMi(s.radiusKm)));
          setBandSource(s.bandSource);
          setInstruments(s.instruments);
          setShows(s.showsPlayed);
          if (s.bands.length) {
            const full = await Promise.all(s.bands.map(async (b) => ({ ...(await api.band(b.mbid)), top: b.top })));
            setCustom(full);
          }
        }
        const me = await api.me();
        setProfileBands(me.bands.length);
        // Default a new search to the profile's own city.
        if (!id && me.city) setLoc({ city: me.city, lat: me.lat, lng: me.lng });
      } catch (e: any) { setError(e.message); } finally { setLoading(false); }
    })();
  }, [id]);

  const criteria = (): SearchCriteria => ({
    minAge: minAge ? Number(minAge) : null,
    maxAge: maxAge ? Number(maxAge) : null,
    city: loc.city, lat: loc.lat, lng: loc.lng,
    radiusKm: miToKm(radiusMi),
    bandSource,
    bands: bandSource === 'custom' ? custom.map((b) => ({ mbid: b.mbid, top: b.top })) : [],
    instruments,
    showsPlayed: shows,
  });

  const validate = (): string | null => {
    if (bandSource === 'custom' && custom.length === 0) return 'Add at least one band for a custom search';
    if (minAge && maxAge && Number(minAge) > Number(maxAge)) return 'Min age must be at most max age';
    return null;
  };

  const run = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setBusy(true);
    setError(null);
    try {
      const { results } = await api.runSearch(criteria());
      nav('/results', { state: { results, name: name || 'Unsaved search' } });
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  const save = async () => {
    const v = validate() ?? (name.trim() ? null : 'Give the search a name to save it');
    if (v) { setError(v); return; }
    setBusy(true);
    setError(null);
    try {
      const s = id ? await api.updateSearch(id, name.trim(), criteria()) : await api.createSearch(name.trim(), criteria());
      nav(`/searches/${s.id}/results`, { replace: true });
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  if (loading) return <div className="spinner">Loading…</div>;

  return (
    <div className="stack">
      <div className="card">
        <label className="field"><span>Search name</span><input type="text" placeholder="e.g. Drummers near me" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} /></label>
      </div>
      <div className="card">
        <h2>Age range</h2>
        <div className="range">
          <input type="number" inputMode="numeric" placeholder="Min" min={13} max={120} value={minAge} onChange={(e) => setMinAge(e.target.value)} />
          <span className="muted">to</span>
          <input type="number" inputMode="numeric" placeholder="Max" min={13} max={120} value={maxAge} onChange={(e) => setMaxAge(e.target.value)} />
        </div>
      </div>
      <div className="card">
        <h2>Location</h2>
        <LocationPicker value={loc} onChange={setLoc} />
        {loc.city && (
          <label className="field" style={{ marginTop: 10 }}>
            <span>Within {radiusMi} miles</span>
            <input type="range" min={5} max={300} step={5} value={radiusMi} onChange={(e) => setRadiusMi(Number(e.target.value))} style={{ width: '100%' }} />
          </label>
        )}
        {!loc.city && <div className="muted small" style={{ marginTop: 8 }}>No location: results are not limited by distance.</div>}
      </div>
      <div className="card">
        <h2>Match against</h2>
        <div className="seg" style={{ marginBottom: 10 }}>
          <button className={bandSource === 'profile' ? 'on' : ''} onClick={() => setBandSource('profile')}>My profile bands</button>
          <button className={bandSource === 'custom' ? 'on' : ''} onClick={() => setBandSource('custom')}>A custom set</button>
        </div>
        {bandSource === 'custom' ? (
          <>
            <div className="muted small" style={{ marginBottom: 8 }}>Pick bands for the project you want to start. ★ marks the ones that matter most.</div>
            <BandPicker value={custom} onChange={setCustom} />
          </>
        ) : (
          profileBands === 0 ? (
            <div className="error small">You haven't added any bands to your profile yet, so results will be ranked by genres, what you're looking for, and distance. <Link to="/profile">Add bands</Link> for taste matching.</div>
          ) : (
            <div className="muted small">Uses the bands on your profile, with your top bands weighted higher.</div>
          )
        )}
      </div>
      <div className="card">
        <h2>They should play (optional)</h2>
        <TagPicker value={instruments} onChange={setInstruments} suggest={(q) => api.suggestInstruments(q).then((r) => r.results)} presets={presets.slice(0, 8)} placeholder="Any instrument…" max={20} hint="Matches people who play at least one of these." />
      </div>
      <div className="card">
        <h2>Shows played (optional)</h2>
        <div className="chips">
          {SHOWS_PLAYED.map((v) => (
            <button type="button" key={v} className={`chip ${shows.includes(v) ? 'on' : ''}`} onClick={() => setShows(shows.includes(v) ? shows.filter((x) => x !== v) : [...shows, v])}>{SHOWS_LABEL[v]}</button>
          ))}
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      <div className="row">
        <button className="btn secondary" style={{ flex: 1 }} disabled={busy} onClick={run}>Run without saving</button>
        <button className="btn" style={{ flex: 1 }} disabled={busy} onClick={save}>{busy ? 'Working…' : id ? 'Save & run' : 'Save & run'}</button>
      </div>
    </div>
  );
}
